import type { Observation, Reference } from '@medplum/fhirtypes'
import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { formatDateTime } from '@seen/core/dates'
import { ExtendedObservation } from '@seen/core/models/observation'
import { resolveId } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { DotSeparator } from '@seen/ui'
import type { JSX } from 'react'
import { Loader, TextLink } from '#src/components/index.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function ObservationDisplayLink({
  reference,
  omitDateTime = false,
}: {
  reference: Reference<Observation>
  omitDateTime?: boolean
}): JSX.Element | null {
  const { data: observation, isLoading } = useRead(
    ExtendedObservation,
    reference
  )

  if (isLoading) {
    return <Loader />
  }

  if (!observation) {
    return null
  }

  const patientId =
    isOfType(observation.subject, 'Patient') && resolveId(observation.subject)

  return (
    <p className="text-xs">
      <TextLink
        to={
          patientId
            ? `/Patient/${patientId}/observations/${observation.id}`
            : '#'
        }
      >
        {displayFromCodeableConcept(observation.codeableConcept)}
        <span className="font-semibold">
          {': '}
          {observation.valueDisplay}
        </span>
      </TextLink>
      {!omitDateTime && observation.effectiveDateTime && (
        <span className="select-none">
          <DotSeparator />
          <span className="text-foreground-tertiary">
            {formatDateTime(observation.effectiveDateTime)}
          </span>
        </span>
      )}
    </p>
  )
}
